import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  CBadge,
  CButton,
  CCallout,
  CFormLabel,
  CFormSelect,
  COffcanvas,
  COffcanvasBody,
  COffcanvasHeader,
  COffcanvasTitle,
  CSmartTable,
} from "@coreui/react-pro";
import EmployerPageFrame from "../../components/employer/EmployerPageFrame";
import useEmployerOsWorkspace from "../../hooks/useEmployerOsWorkspace";
import { buildCandidateCaseRows, buildDocumentRows, formatDateTime } from "../../services/employer/employerCaseOsDomain";
import { formatEmployerAdFocusLabel } from "../../utils/employerAdLabels";

export default function EmployerDocumentsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const workspace = useEmployerOsWorkspace({ preloadDetails: true, preloadFiling: true });
  const [activeApplicationId, setActiveApplicationId] = useState(searchParams.get("applicationId") || "");
  const [activeDocumentId, setActiveDocumentId] = useState("");
  const requestedAdId = searchParams.get("adId") || "";
  const requestedApplicationId = searchParams.get("applicationId") || "";

  useEffect(() => {
    if (requestedAdId) workspace.setSelectedAdId(requestedAdId);
  }, [requestedAdId, workspace.setSelectedAdId]);

  useEffect(() => {
    if (requestedApplicationId) {
      setActiveApplicationId(requestedApplicationId);
    }
  }, [requestedApplicationId]);

  useEffect(() => {
    if (!workspace.applications.length) return;
    if (activeApplicationId && workspace.applications.some((entry) => String(entry.id) === String(activeApplicationId))) return;
    setActiveApplicationId(String(workspace.applications[0]?.id || ""));
  }, [activeApplicationId, workspace.applications]);

  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    if (workspace.selectedAdId) {
      next.set("adId", workspace.selectedAdId);
    } else {
      next.delete("adId");
    }
    if (activeApplicationId) {
      next.set("applicationId", activeApplicationId);
    } else {
      next.delete("applicationId");
    }
    const currentSerialized = searchParams.toString();
    const nextSerialized = next.toString();
    if (currentSerialized !== nextSerialized) {
      setSearchParams(next, { replace: true });
    }
  }, [activeApplicationId, searchParams, setSearchParams, workspace.selectedAdId]);

  const caseRows = useMemo(
    () => buildCandidateCaseRows({
      ads: workspace.ads,
      applications: workspace.applications,
      detailsById: workspace.detailsById,
      filingById: workspace.filingById,
    }),
    [workspace.ads, workspace.applications, workspace.detailsById, workspace.filingById]
  );
  const documentRows = useMemo(() => buildDocumentRows({ caseRows }), [caseRows]);
  const filteredRows = useMemo(
    () => documentRows.filter((entry) => !activeApplicationId || String(entry.applicationId) === String(activeApplicationId)),
    [activeApplicationId, documentRows]
  );
  const activeDocument = filteredRows.find((entry) => entry.id === activeDocumentId) || null;

  return (
    <EmployerPageFrame
      title="Dokumente"
      subtitle="Der Dokumentenhub fuehrt Consent, Verifikationsstand, AI-/Pruefzusammenfassung und Originalzugriff in einer kontrollierten Surface zusammen."
      actions={(
        <>
          <CButton color="secondary" variant="outline" onClick={() => workspace.refreshWorkspace()}>
            Aktualisieren
          </CButton>
          <CButton
            color="primary"
            variant="outline"
            onClick={() => navigate(`/dashboard/employer/candidates${workspace.selectedAdId ? `?adId=${workspace.selectedAdId}${workspace.isTraining ? "&training=1" : ""}` : ""}`)}
          >
            Kandidaten
          </CButton>
        </>
      )}
      contentClassName="space-y-4"
      shellProps={{
        shellLabel: "Employer Document Hub",
        shellDescription: "Dokumentenstatus, Consent und Originalzugriff folgen derselben auditierbaren Produktgrammatik.",
        topContext: "Dokumente",
      }}
    >
      {workspace.error ? (
        <CCallout color="danger">
          <strong>Dokumentenhub konnte nicht vollstaendig geladen werden.</strong>
          <div>{workspace.error}</div>
        </CCallout>
      ) : null}

      <section className="dashboard-card-shell space-y-4">
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
          <div>
            <CFormLabel>Anzeige</CFormLabel>
            <CFormSelect
              value={workspace.selectedAdId}
              onChange={(event) => workspace.setSelectedAdId(event.target.value)}
              options={(workspace.ads || []).map((entry) => ({
                label: formatEmployerAdFocusLabel(entry),
                value: String(entry.id),
              }))}
            />
          </div>
          <div>
            <CFormLabel>Case</CFormLabel>
            <CFormSelect
              value={activeApplicationId}
              onChange={(event) => setActiveApplicationId(event.target.value)}
              options={(workspace.applications || []).map((entry) => ({
                label: `${entry.candidate_ref || "CAND-pending"} · ${entry.status || "offen"}`,
                value: String(entry.id),
              }))}
            />
          </div>
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
            <span className="dashboard-summary-label">Originalzugriff</span>
            <strong>{filteredRows.filter((entry) => entry.availableOriginal).length}</strong>
            <p className="mb-0 text-sm text-slate-600">Originaldokumente bleiben nur dort klickbar, wo eine sichtbare Freigabe vorliegt.</p>
          </div>
        </div>

        {filteredRows.length ? (
          <CSmartTable
            columns={[
              { key: "candidateRef", label: "Candidate" },
              { key: "section", label: "Bereich" },
              { key: "title", label: "Dokument" },
              { key: "statusLabel", label: "Unterlagenstatus" },
              { key: "consentStatusLabel", label: "Consent / Zugriff" },
              { key: "verificationStatus", label: "Pruefzusammenfassung" },
              { key: "responsibility", label: "Verantwortung" },
              { key: "actions", label: "Aktion", sorter: false, filter: false },
            ]}
            items={filteredRows}
            pagination={false}
            sorter={false}
            tableProps={{ hover: true, responsive: true }}
            scopedColumns={{
              section: (item) => (
                <td>
                  <div className="d-flex flex-column gap-1">
                    <strong>{item.section}</strong>
                    <small className="text-slate-500">{item.required ? "Pflichtunterlage" : "Support"}</small>
                  </div>
                </td>
              ),
              statusLabel: (item) => (
                <td>
                  <CBadge color={item.statusColor}>{item.statusLabel}</CBadge>
                </td>
              ),
              consentStatusLabel: (item) => (
                <td>
                  <div className="d-flex flex-column gap-1">
                    <CBadge color={item.consentStatusColor}>{item.consentStatusLabel}</CBadge>
                    <small className="text-slate-500">{item.availableOriginal ? "Original sichtbar" : "nur Metadaten / Status"}</small>
                  </div>
                </td>
              ),
              actions: (item) => (
                <td>
                  <div className="d-flex flex-wrap gap-2">
                    <CButton color="secondary" size="sm" variant="outline" onClick={() => setActiveDocumentId(item.id)}>
                      Detail
                    </CButton>
                    {item.availableOriginal ? (
                      <CButton
                        color="primary"
                        size="sm"
                        variant="outline"
                        onClick={() => workspace.adapter.downloadAuthorityDocument({ applicationId: item.applicationId, documentEntry: item.raw })}
                      >
                        Original oeffnen
                      </CButton>
                    ) : null}
                  </div>
                </td>
              ),
            }}
          />
        ) : (
          <CCallout color="info">
            <strong>Keine Dokumente im aktuellen Fokus.</strong>
            <div>Waehle einen anderen Case oder oeffne zuerst den Kandidatenbereich fuer das betreffende Filing.</div>
          </CCallout>
        )}
      </section>

      <COffcanvas placement="end" visible={Boolean(activeDocument)} onHide={() => setActiveDocumentId("")}>
        <COffcanvasHeader closeButton>
          <COffcanvasTitle>{activeDocument?.title || "Dokumentdetail"}</COffcanvasTitle>
        </COffcanvasHeader>
        <COffcanvasBody>
          {activeDocument ? (
            <div className="d-grid gap-3">
              <CCallout color={activeDocument.availableOriginal ? "success" : "warning"}>
                <strong>{activeDocument.consentStatusLabel}</strong>
                <div>
                  {activeDocument.availableOriginal
                    ? "Das Original ist ueber eine sichtbare Freigabe erreichbar."
                    : "Ohne sichtbare Freigabe bleibt nur Status, Bucket und Pruefzusammenfassung sichtbar."}
                </div>
              </CCallout>
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                <span className="dashboard-summary-label">Case-Bereich</span>
                <strong>{activeDocument.section}</strong>
                <p className="mb-0 text-sm text-slate-600">{activeDocument.required ? "Pflichtunterlage im Case OS." : "Unterstuetzende Unterlage."}</p>
              </div>
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                <span className="dashboard-summary-label">Candidate</span>
                <strong>{activeDocument.candidateRef}</strong>
                <p className="mb-0 text-sm text-slate-600">{activeDocument.adTitle}</p>
              </div>
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                <span className="dashboard-summary-label">Status</span>
                <strong>{activeDocument.statusLabel}</strong>
                <p className="mb-0 text-sm text-slate-600">{activeDocument.verificationStatus}</p>
              </div>
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                <span className="dashboard-summary-label">Naechste Aktion</span>
                <strong>{activeDocument.nextAction}</strong>
                <p className="mb-0 text-sm text-slate-600">Zuletzt im Hub sichtbar: {formatDateTime(activeDocument.caseRow?.updatedAt)}</p>
              </div>
              {activeDocument.availableOriginal ? (
                <CButton
                  color="primary"
                  onClick={() => workspace.adapter.downloadAuthorityDocument({ applicationId: activeDocument.applicationId, documentEntry: activeDocument.raw })}
                >
                  Originaldokument herunterladen
                </CButton>
              ) : null}
            </div>
          ) : null}
        </COffcanvasBody>
      </COffcanvas>
    </EmployerPageFrame>
  );
}
